// Campaigns (SPEC §4 Campaigns.tsx) — CRUD on the campaigns table with an
// active toggle, an editor (trigger type: weeks-since-last-visit or segment
// blast, {first_name} template with live preview, cooldown, send hour), a
// "Run now" button that hits the worker's /campaigns/run endpoint, and a
// recent-sends table from campaign_sends. Sends are joined to campaign and
// client names client-side (targeted lookups) rather than relying on
// PostgREST FK embedding, since FK column types are created dynamically.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { runCampaignsNow } from "../lib/api";
import { fmtDateTime } from "../lib/dates";
import Modal from "../components/Modal";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import type { Campaign, CampaignSend, CampaignTriggerType, Id, Segment } from "../lib/types";

/* ------------------------------------------------------------------- editor */

interface CampaignForm {
  name: string;
  trigger_type: CampaignTriggerType;
  trigger_weeks: string;
  segment_id: string;
  template: string;
  cooldown_days: string;
  send_hour_local: string;
  active: boolean;
}

const DEFAULT_FORM: CampaignForm = {
  name: "",
  trigger_type: "weeks_since_last_visit",
  trigger_weeks: "6",
  segment_id: "",
  template: "",
  cooldown_days: "90",
  send_hour_local: "11",
  active: false,
};

function campaignToForm(c: Campaign): CampaignForm {
  return {
    name: c.name,
    trigger_type: c.trigger_type,
    trigger_weeks: c.trigger_weeks?.toString() ?? "6",
    segment_id: c.segment_id ?? "",
    template: c.template,
    cooldown_days: c.cooldown_days.toString(),
    send_hour_local: c.send_hour_local.toString(),
    active: c.active,
  };
}

function intIn(value: string, min: number, max: number): number | null {
  const n = Number(value.trim());
  if (!value.trim() || !Number.isInteger(n) || n < min || n > max) return null;
  return n;
}

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`,
}));

interface EditorProps {
  initial: Campaign | null;
  segments: Segment[];
  onClose: () => void;
  onSaved: () => void;
}

function CampaignEditor({ initial, segments, onClose, onSaved }: EditorProps) {
  const [form, setForm] = useState<CampaignForm>(initial ? campaignToForm(initial) : DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof CampaignForm>(key: K, value: CampaignForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const preview = form.template.replace(/\{first_name\}/g, "Sarah");

  async function save() {
    setError(null);
    const name = form.name.trim();
    if (!name) return setError("Give the campaign a name.");
    const template = form.template.trim();
    if (!template) return setError("Write the message template.");
    const cooldown = intIn(form.cooldown_days, 0, 3650);
    if (cooldown == null) return setError("Cooldown must be a whole number of days (0 or more).");
    const hour = intIn(form.send_hour_local, 0, 23);
    if (hour == null) return setError("Send hour must be 0–23.");

    let trigger_weeks: number | null = null;
    let segment_id: string | null = null;
    if (form.trigger_type === "weeks_since_last_visit") {
      trigger_weeks = intIn(form.trigger_weeks, 1, 520);
      if (trigger_weeks == null) return setError("Weeks since last visit must be a whole number ≥ 1.");
    } else {
      if (!form.segment_id) return setError("Pick the segment to blast.");
      segment_id = form.segment_id;
    }

    const payload = {
      name,
      active: form.active,
      trigger_type: form.trigger_type,
      trigger_weeks,
      segment_id,
      template,
      cooldown_days: cooldown,
      send_hour_local: hour,
    };

    setSaving(true);
    try {
      if (initial) {
        const { error: err } = await supabase.from("campaigns").update(payload).eq("id", initial.id);
        if (err) throw new Error(err.message);
      } else {
        const { error: err } = await supabase.from("campaigns").insert(payload);
        if (err) throw new Error(err.message);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      wide
      title={initial ? `Edit campaign: ${initial.name}` : "New campaign"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save campaign"}
          </button>
        </>
      }
    >
      {error && <div className="error-banner">{error}</div>}

      <div className="form-row">
        <div className="field" style={{ flex: 2 }}>
          <label htmlFor="camp-name">Name</label>
          <input
            id="camp-name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. 6-week rebook nudge"
            disabled={saving}
          />
        </div>
        <div className="field">
          <label htmlFor="camp-active">Status</label>
          <label className="checkbox" htmlFor="camp-active" style={{ paddingTop: 6 }}>
            <span className="switch">
              <input
                id="camp-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
                disabled={saving}
              />
              <span className="switch-slider" />
            </span>
            {form.active ? "Active" : "Paused"}
          </label>
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="camp-type">Trigger</label>
          <select
            id="camp-type"
            value={form.trigger_type}
            onChange={(e) => set("trigger_type", e.target.value as CampaignTriggerType)}
            disabled={saving}
          >
            <option value="weeks_since_last_visit">Weeks since last visit</option>
            <option value="segment_blast">Segment blast</option>
          </select>
        </div>
        {form.trigger_type === "weeks_since_last_visit" ? (
          <div className="field">
            <label htmlFor="camp-weeks">Weeks since last visit</label>
            <input
              id="camp-weeks"
              type="number"
              min={1}
              step={1}
              value={form.trigger_weeks}
              onChange={(e) => set("trigger_weeks", e.target.value)}
              disabled={saving}
            />
          </div>
        ) : (
          <div className="field">
            <label htmlFor="camp-segment">Segment</label>
            <select
              id="camp-segment"
              value={form.segment_id}
              onChange={(e) => set("segment_id", e.target.value)}
              disabled={saving}
            >
              <option value="">Pick a segment…</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="field">
        <label htmlFor="camp-template">Message template</label>
        <textarea
          id="camp-template"
          value={form.template}
          onChange={(e) => set("template", e.target.value)}
          placeholder="Hi {first_name}! It's been a while since your last drip…"
          disabled={saving}
        />
        <span className="field-hint">
          {"{first_name}"} is replaced per recipient · {form.template.length} characters
        </span>
      </div>

      {form.template.trim() && (
        <div className="panel mb-2">
          <div className="panel-title">Preview (as Sarah would see it)</div>
          <div className="bubble bubble-out" style={{ maxWidth: "100%", alignSelf: "auto" }}>
            {preview}
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="field">
          <label htmlFor="camp-cooldown">Cooldown (days)</label>
          <input
            id="camp-cooldown"
            type="number"
            min={0}
            step={1}
            value={form.cooldown_days}
            onChange={(e) => set("cooldown_days", e.target.value)}
            disabled={saving}
          />
          <span className="field-hint">A client is never texted twice by this campaign within the cooldown.</span>
        </div>
        <div className="field">
          <label htmlFor="camp-hour">Send hour (NY time)</label>
          <select
            id="camp-hour"
            value={form.send_hour_local}
            onChange={(e) => set("send_hour_local", e.target.value)}
            disabled={saving}
          >
            {HOURS.map((h) => (
              <option key={h.value} value={String(h.value)}>
                {h.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------------- page */

interface ClientNameRow {
  id: Id;
  first_name: string | null;
  last_name: string | null;
}

export default function Campaigns() {
  const [editing, setEditing] = useState<Campaign | null | "new">(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());

  const campaignsQ = useQuery<Campaign[]>(async () => {
    const { data, error } = await supabase
      .from("campaigns")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as Campaign[];
  }, []);

  const segmentsQ = useQuery<Segment[]>(async () => {
    const { data, error } = await supabase.from("segments").select("*").order("name");
    if (error) throw new Error(error.message);
    return (data ?? []) as Segment[];
  }, []);

  const sendsQ = useQuery<CampaignSend[]>(async () => {
    const { data, error } = await supabase
      .from("campaign_sends")
      .select("*")
      .order("sent_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return (data ?? []) as CampaignSend[];
  }, []);

  // Resolve client names for the recent-sends table with one targeted lookup.
  useEffect(() => {
    const sends = sendsQ.data;
    if (!sends || sends.length === 0) return;
    const ids = [...new Set(sends.map((s) => String(s.client_id)))];
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id,first_name,last_name")
        .in("id", ids);
      if (cancelled || error || !data) return;
      const m = new Map<string, string>();
      for (const c of data as ClientNameRow[]) {
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
        m.set(String(c.id), name || `Client ${String(c.id)}`);
      }
      setClientNames(m);
    })();
    return () => {
      cancelled = true;
    };
  }, [sendsQ.data]);

  const campaigns = campaignsQ.data ?? [];
  const segments = segmentsQ.data ?? [];
  const segmentNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of segments) m.set(s.id, s.name);
    return m;
  }, [segments]);
  const campaignNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of campaigns) m.set(c.id, c.name);
    return m;
  }, [campaigns]);

  async function toggleActive(c: Campaign) {
    setTogglingId(c.id);
    setActionError(null);
    try {
      const { error } = await supabase
        .from("campaigns")
        .update({ active: !c.active })
        .eq("id", c.id);
      if (error) throw new Error(error.message);
      campaignsQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setTogglingId(null);
    }
  }

  async function remove(c: Campaign) {
    if (!window.confirm(`Delete campaign "${c.name}"? Its send history is kept.`)) return;
    setActionError(null);
    const { error } = await supabase.from("campaigns").delete().eq("id", c.id);
    if (error) {
      setActionError(error.message);
      return;
    }
    setNotice(`Deleted "${c.name}".`);
    campaignsQ.reload();
  }

  async function runNow() {
    setRunning(true);
    setNotice(null);
    setActionError(null);
    try {
      const res = await runCampaignsNow();
      const sent = typeof res.total_sent === "number" ? res.total_sent : null;
      const errors = typeof res.total_errors === "number" ? res.total_errors : 0;
      const parts: string[] = [];
      if (sent != null) parts.push(`sent ${sent} message${sent === 1 ? "" : "s"}`);
      if (errors > 0) parts.push(`${errors} error${errors === 1 ? "" : "s"}`);
      if (res.capped) parts.push(`stopped at the ${res.cap}-send cap`);
      if (res.rate_limited) {
        const wait = res.retry_after_seconds ?? 60;
        parts.push(`stopped by the Project Blue rate limit — retry in ~${wait}s (undelivered clients go out on the next hourly run)`);
      }
      setNotice(parts.length > 0 ? `Run complete — ${parts.join(" · ")}.` : "Run complete.");
      sendsQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  }

  function describeTrigger(c: Campaign): string {
    if (c.trigger_type === "weeks_since_last_visit") {
      return `${c.trigger_weeks ?? "?"} weeks since last visit`;
    }
    const segName = c.segment_id ? (segmentNames.get(c.segment_id) ?? "deleted segment") : "no segment";
    return `Blast: ${segName}`;
  }

  const campaignColumns: Column<Campaign>[] = [
    {
      key: "active",
      label: "Active",
      width: "70px",
      sortValue: (c) => (c.active ? 1 : 0),
      render: (c) => (
        <span className="switch" title={c.active ? "Active — sends daily at its send hour" : "Paused"}>
          <input
            type="checkbox"
            checked={c.active}
            disabled={togglingId === c.id}
            onChange={() => void toggleActive(c)}
            aria-label={`Toggle ${c.name}`}
          />
          <span className="switch-slider" />
        </span>
      ),
    },
    {
      key: "name",
      label: "Name",
      render: (c) => <span className="bold">{c.name}</span>,
    },
    {
      key: "trigger",
      label: "Trigger",
      sortValue: (c) => describeTrigger(c),
      render: (c) => (
        <span className={c.trigger_type === "segment_blast" ? "badge badge-accent" : "badge badge-muted"}>
          {describeTrigger(c)}
        </span>
      ),
    },
    {
      key: "template",
      label: "Template",
      render: (c) => (
        <span className="muted truncate" style={{ display: "inline-block", maxWidth: 280 }} title={c.template}>
          {c.template}
        </span>
      ),
      sortValue: (c) => c.template,
    },
    {
      key: "cooldown_days",
      label: "Cooldown",
      align: "right",
      width: "90px",
      render: (c) => <>{c.cooldown_days}d</>,
    },
    {
      key: "send_hour_local",
      label: "Send hour",
      align: "right",
      width: "90px",
      render: (c) => <>{((c.send_hour_local + 11) % 12) + 1}{c.send_hour_local < 12 ? "am" : "pm"}</>,
    },
    {
      key: "actions",
      label: "Actions",
      width: "150px",
      render: (c) => (
        <span className="flex nowrap">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(c)}>
            Edit
          </button>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => void remove(c)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  const sends = sendsQ.data ?? [];
  const sendColumns: Column<CampaignSend>[] = [
    {
      key: "sent_at",
      label: "Sent",
      width: "180px",
      render: (s) => <span className="nowrap">{fmtDateTime(s.sent_at)}</span>,
    },
    {
      key: "campaign_id",
      label: "Campaign",
      sortValue: (s) => campaignNames.get(s.campaign_id) ?? s.campaign_id,
      render: (s) => <>{campaignNames.get(s.campaign_id) ?? <span className="muted">deleted campaign</span>}</>,
    },
    {
      key: "client_id",
      label: "Client",
      sortValue: (s) => clientNames.get(String(s.client_id)) ?? String(s.client_id),
      render: (s) => (
        <Link to={`/clients/${String(s.client_id)}`}>
          {clientNames.get(String(s.client_id)) ?? `Client ${String(s.client_id)}`}
        </Link>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Campaigns</h1>
          <div className="page-sub">
            Automated SMS — the worker runs active campaigns daily at their send hour (NY).
          </div>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void runNow()}
            disabled={running}
            title="Trigger the scheduler once, right now (respects cooldowns, opt-outs, and snoozes)"
          >
            {running ? "Running…" : "Run now"}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing("new")}>
            New campaign
          </button>
        </div>
      </div>

      {notice && <div className="success-banner">{notice}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}
      {campaignsQ.error && <div className="error-banner">{campaignsQ.error}</div>}

      {campaignsQ.loading && !campaignsQ.data && (
        <div className="loading">
          <span className="spinner" /> Loading campaigns…
        </div>
      )}

      {campaignsQ.data && campaigns.length === 0 && (
        <div className="empty-state">
          No campaigns yet. Create one to start automatic rebook nudges.
        </div>
      )}

      {campaigns.length > 0 && <DataTable columns={campaignColumns} rows={campaigns} />}

      <h2 className="mt-2">Recent sends</h2>
      {sendsQ.error && <div className="error-banner">{sendsQ.error}</div>}
      {sendsQ.loading && !sendsQ.data ? (
        <div className="loading">
          <span className="spinner" /> Loading send history…
        </div>
      ) : (
        <DataTable
          columns={sendColumns}
          rows={sends}
          defaultSortKey="sent_at"
          defaultSortDir="desc"
          emptyMessage="No campaign sends yet."
        />
      )}

      {editing !== null && (
        <CampaignEditor
          initial={editing === "new" ? null : editing}
          segments={segments}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice("Campaign saved.");
            campaignsQ.reload();
          }}
        />
      )}
    </div>
  );
}
